import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ─── Types ───────────────────────────────────────────────────────────

export type WeekSegment = "actual" | "forecast" | "extension";
export type NoteKind = "ai" | "human";

export interface CashFlowRun {
  id: string;
  label: string;
  prepared_on: string;
  actuals_through: string | null;
  source_file: string | null;
  collections_run_rate: number | null;
  is_current: boolean;
  /** Last week every driver still had a scheduled amount. Weeks after it
   *  understate cash movement — a driver whose schedule ran out contributes
   *  zero rather than repeating, which reads as good news. */
  complete_through: string | null;
  warnings: string[] | null;
  notes: string | null;
}

export interface CashFlowWeek {
  week_ending: string;
  segment: WeekSegment;
  total_inflows: number;
  total_outflows: number;
  net_change: number;
  ending_cash: number;
}

export interface CashFlowMonth {
  month: string;
  actual: number;
  estimated_tail: number;
}

export interface NoteLlmMeta {
  model?: string;
  prompt_version?: string;
  trigger?: "user" | "scheduled";
  context_hash?: string;
}

export interface CashFlowNote {
  id: string;
  run_id: string | null;
  week_ending: string | null;
  kind: NoteKind;
  body: string;
  author_name: string | null;
  llm_meta: NoteLlmMeta | null;
  updated_at: string;
}

export type AiProvider = "ollama" | "anthropic" | "openai";

export interface AiModel {
  id: string;
  provider: AiProvider;
  name: string;
  is_default: boolean;
}

export interface AiModelsResponse {
  models: AiModel[];
  default_id: string | null;
}

export interface CashFlowSettings {
  cash_threshold: number;
  updated_by_name: string | null;
  updated_at: string;
}

export interface CashFlowSummary {
  runs: CashFlowRun[];
  run: CashFlowRun | null;
  weeks: CashFlowWeek[];
  months: CashFlowMonth[];
  notes: CashFlowNote[];
  settings: CashFlowSettings;
}

export interface NoteUpsertPayload {
  run_id: string;
  week_ending: string | null;
  kind: NoteKind;
  body: string;
}

// ─── Ingestion ───────────────────────────────────────────────────────

export type IngestSource = "graph" | "manual";
export type IngestStatus = "running" | "ok" | "partial" | "failed";
export type BankFileStatus = "pending" | "parsed" | "failed" | "unmatched";
export type BucketKind = "inflow" | "outflow" | "memo";

export interface BankFileRow {
  id: string;
  filename: string;
  account_code: string | null;
  parser: string | null;
  status: BankFileStatus;
  row_count: number;
  byte_size: number;
  error: string | null;
}

export interface IngestRun {
  id: string;
  week_folder: string;
  source: IngestSource;
  status: IngestStatus;
  files_matched: number;
  rows_ingested: number;
  accounts_missing: string[];
  tie_out_week: string | null;
  /** Model ending cash minus bank statement total. 0 = agrees with the workbook. */
  tie_out_variance: number | null;
  statement_total: number | null;
  unmapped_count: number;
  /** Not errors, but things someone has to see — chiefly a file whose coverage
   *  ends before rows already stored for that account, which is what a stale
   *  or backdated export looks like. Null on runs that predate the column. */
  warnings: string[] | null;
  error: string | null;
  created_at: string;
  files: BankFileRow[];
}

export interface SourceStatus {
  configured: boolean;
  folder: string;
  available_weeks: string[];
  ingested_weeks: string[];
  error: string | null;
}

export interface CashBucket {
  id: string;
  name: string;
  kind: BucketKind;
  sort_order: number;
}

export interface UnmappedGroup {
  description: string;
  count: number;
  total_amount: number;
  first_seen: string;
  last_seen: string;
  /** A transaction in the group — what the classify action acts on. */
  sample_transaction_id: string;
  sample_row_hash: string;
  /** Vendor key used for grouping; pre-fills the rule pattern. */
  suggested_pattern: string;
}

export interface PatternPreview {
  valid: boolean;
  error: string | null;
  match_count: number;
  already_classified: number;
  /** An existing rule currently claiming these rows; the new rule is slotted ahead of it. */
  shadowed_by: string | null;
  samples: string[];
}

export interface AssignBucketPayload {
  transaction_id: string;
  bucket_id: string;
  reason?: string | null;
  /** Set to also create a mapping rule and re-bucket history. */
  create_rule_pattern?: string | null;
}

export interface ReapplyResult {
  scanned: number;
  rebucketed: number;
  overrides_reapplied: number;
}

// ─── Hooks ───────────────────────────────────────────────────────────

const KEY = ["cash_flow"];

export function useCashFlowSummary(runId?: string) {
  return useQuery({
    queryKey: [...KEY, "summary", runId ?? "current"],
    queryFn: () =>
      apiFetch<CashFlowSummary>(
        `/cash-flow/summary${runId ? `?run_id=${encodeURIComponent(runId)}` : ""}`,
      ),
  });
}

export function useUpsertNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: NoteUpsertPayload) =>
      apiFetch<CashFlowNote | null>(`/cash-flow/notes`, { method: "PUT", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAiModels(enabled = true) {
  return useQuery({
    queryKey: [...KEY, "ai_models"],
    queryFn: () => apiFetch<AiModelsResponse>(`/cash-flow/ai/models`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGenerateAiNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { run_id: string; week_ending: string | null; model_id: string | null }) =>
      apiFetch<CashFlowNote>(`/cash-flow/ai/generate`, { method: "POST", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { cash_threshold: number }) =>
      apiFetch<CashFlowSettings>(`/cash-flow/settings`, { method: "PUT", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── Ingestion hooks ─────────────────────────────────────────────────

/** Polls SharePoint on load, so a stale "no new weeks" reading is unlikely. */
export function useIngestStatus() {
  return useQuery({
    queryKey: [...KEY, "ingest_status"],
    queryFn: () => apiFetch<SourceStatus>(`/cash-flow/ingest/status`),
    staleTime: 60 * 1000,
  });
}

export function useIngestRuns(limit = 25) {
  return useQuery({
    queryKey: [...KEY, "ingest_runs", limit],
    queryFn: () => apiFetch<IngestRun[]>(`/cash-flow/ingest/runs?limit=${limit}`),
  });
}

export function useCashBuckets() {
  return useQuery({
    queryKey: [...KEY, "buckets"],
    queryFn: () => apiFetch<CashBucket[]>(`/cash-flow/buckets`),
    staleTime: 10 * 60 * 1000,
  });
}

export function useUnmapped() {
  return useQuery({
    queryKey: [...KEY, "unmapped"],
    queryFn: () => apiFetch<UnmappedGroup[]>(`/cash-flow/unmapped`),
  });
}

/** Pull a week from the FP&A SharePoint library. Omit the week for the newest. */
export function useIngestFromSharePoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { week_folder?: string }) =>
      apiFetch<IngestRun>(`/cash-flow/ingest/graph`, { method: "POST", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUploadBankFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ weekFolder, files }: { weekFolder: string; files: File[] }) => {
      const form = new FormData();
      form.append("week_folder", weekFolder);
      files.forEach((f) => form.append("files", f, f.name));
      return apiFetch<IngestRun>(`/cash-flow/ingest/upload`, { method: "POST", body: form });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Dry-run a rule pattern against real bank history before saving it. */
export function useRulePreview(pattern: string, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "pattern_preview", pattern],
    queryFn: () =>
      apiFetch<PatternPreview>(`/cash-flow/unmapped/preview`, {
        method: "POST",
        body: { pattern },
      }),
    enabled: enabled && pattern.trim().length > 0,
  });
}

export function useAssignBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignBucketPayload) =>
      apiFetch<void>(`/cash-flow/unmapped/assign`, { method: "POST", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── Mapping + Assumptions ───────────────────────────────────────────

export interface BucketRule {
  id: string;
  pattern: string;
  bucket_id: string;
  bucket_name: string | null;
  direction: string | null;
  notes: string | null;
  /** Evaluation order — rules are first-match-wins, so this is semantics. */
  priority: number;
  is_active: boolean;
  updated_by_name: string | null;
  updated_at: string;
}

export interface ChangeLogEntry {
  id: string;
  changed_on: string;
  change: string;
  why: string | null;
  impact: string | null;
  author_name: string | null;
  created_at: string;
}

export interface DriverWeek {
  week_ending: string;
  amount: number;
  is_override: boolean;
  note: string | null;
}

export type DriverMethod =
  | "manual"
  | "seasonality"
  | "pay_schedule"
  | "recurring_vendors"
  | "new_revenue";

export interface ForecastDriver {
  id: string;
  bucket_id: string;
  bucket_name: string | null;
  label: string;
  method: DriverMethod;
  config: Record<string, unknown>;
  sort_order: number;
  is_active: boolean;
  notes: string | null;
  weeks: DriverWeek[];
}

export interface PayScheduleRow {
  id: string;
  period_start: string;
  period_end: string;
  approve_by: string | null;
  payment_date: string;
  cash_week: string;
  pay_type: "Mid" | "End";
  forecast_amount: number;
  notes: string | null;
}

export interface NewHireRow {
  id: string;
  name: string;
  start_date: string;
  add_per_paycheck: number;
  note: string | null;
  is_active: boolean;
}

export interface NewRevenueRow {
  id: string;
  contract: string;
  annual_amount: number;
  start_date: string;
  terms_days: number;
  buffer_days: number;
  first_cash_week: string;
  notes: string | null;
  is_active: boolean;
}

export interface RecurringVendorRow {
  id: string;
  vendor: string;
  due_date: string;
  cash_week: string;
  forecast_amount: number;
  notes: string | null;
}

export interface SeasonalityWeightRow {
  id: string;
  day_of_month: number;
  slot: string;
  weight: number;
}

export interface WeekExclusionRow {
  id: string;
  week_ending: string;
  reason: string;
  author_name: string | null;
}

export interface CollectionsConfig {
  trailing_weeks: number;
  weeks_per_month: number;
  updated_by_name: string | null;
  updated_at: string;
}

export interface Assumptions {
  drivers: ForecastDriver[];
  pay_schedule: PayScheduleRow[];
  new_hires: NewHireRow[];
  new_revenue: NewRevenueRow[];
  recurring_vendors: RecurringVendorRow[];
  seasonality_weights: SeasonalityWeightRow[];
  week_exclusions: WeekExclusionRow[];
  collections_config: CollectionsConfig;
}

export function useBucketRules() {
  return useQuery({
    queryKey: [...KEY, "rules"],
    queryFn: () => apiFetch<BucketRule[]>(`/cash-flow/rules`),
  });
}

export function useAssumptions() {
  return useQuery({
    queryKey: [...KEY, "assumptions"],
    queryFn: () => apiFetch<Assumptions>(`/cash-flow/assumptions`),
  });
}

export interface ProposedWeek {
  week_ending: string;
  amount: number;
}

export interface DriverPlan {
  driver_id: string;
  label: string;
  bucket: string;
  /** weekly | monthly | every N days | none */
  cadence: string;
  covered_through: string | null;
  last_amount: number | null;
  weeks: ProposedWeek[];
  /** Why nothing could be proposed. Shown rather than hidden — a driver that
   *  needs a human is the whole reason this list is reviewed. */
  blocked: string | null;
}

export interface RollPreview {
  cutover: string | null;
  horizon_end: string | null;
  plans: DriverPlan[];
}

export interface RollResult {
  added: number;
  updated: number;
  drivers_touched: number;
}

export function useRollPreview(enabled = true) {
  return useQuery({
    queryKey: [...KEY, "roll_preview"],
    queryFn: () => apiFetch<RollPreview>(`/cash-flow/assumptions/roll`),
    enabled,
  });
}

export function useApplyRoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weeks: { driver_id: string; week_ending: string; amount: number }[]) =>
      apiFetch<RollResult>(`/cash-flow/assumptions/roll`, {
        method: "POST",
        body: { weeks },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpsertDriverWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { driverId: string; week_ending: string; amount: number }) =>
      apiFetch<RollResult>(`/cash-flow/assumptions/drivers/${p.driverId}/weeks`, {
        method: "PUT",
        body: { week_ending: p.week_ending, amount: p.amount },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteDriverWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekId: string) =>
      apiFetch<void>(`/cash-flow/assumptions/driver-weeks/${weekId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useChangeLog() {
  return useQuery({
    queryKey: [...KEY, "change_log"],
    queryFn: () => apiFetch<ChangeLogEntry[]>(`/cash-flow/change-log`),
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      pattern: string;
      bucket_id: string;
      notes?: string | null;
      why?: string | null;
    }) => apiFetch<BucketRule>(`/cash-flow/rules`, { method: "POST", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      apiFetch<BucketRule>(`/cash-flow/rules/${id}`, { method: "PATCH", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReorderRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { ordered_ids: string[]; why?: string | null }) =>
      apiFetch<void>(`/cash-flow/rules/reorder`, { method: "POST", body: payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateCollectionsConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { trailing_weeks: number; why?: string | null }) =>
      apiFetch<CollectionsConfig>(`/cash-flow/assumptions/collections`, {
        method: "PUT",
        body: payload,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── Forecast grid, lock, accuracy ───────────────────────────────────

export type ForecastSegment = "actual" | "forecast";

export interface ForecastGridWeek {
  week_ending: string;
  segment: ForecastSegment;
  /** bucket name -> amount. Outflows negative, matching the workbook. */
  buckets: Record<string, number>;
  total_inflows: number;
  total_outflows: number;
  net_change: number;
  beginning_cash: number;
  ending_cash: number;
}

export interface DriverCoverage {
  label: string;
  bucket: string;
  method: string;
  /** Last week this driver has inputs for; null = unbounded (computed). */
  covered_through: string | null;
}

export interface ForecastGrid {
  actuals_through: string | null;
  collections_run_rate: number;
  /** Last week every scheduled driver still has inputs for. Weeks after this
   *  UNDERSTATE cash movement — drivers don't extrapolate, so one with no
   *  scheduled amount contributes zero rather than repeating. */
  complete_through: string | null;
  coverage: DriverCoverage[];
  weeks: ForecastGridWeek[];
  /** Things the engine could not model, rather than silently zeroing. */
  warnings: string[];
}

export interface ForecastSnapshot {
  id: string;
  week_ending: string;
  trigger: "manual" | "cron_monday" | "backfill";
  captured_at: string;
  is_locked: boolean;
  locked_at: string | null;
  locked_by_name: string | null;
  tie_out_variance: number | null;
  collections_run_rate: number | null;
}

export interface LockResult {
  snapshot: ForecastSnapshot;
  captured_rows: number;
  actualized_rows: number;
  warnings: string[];
}

export interface AccuracyRow {
  target_week_ending: string;
  horizon: "1wk" | "6wk" | "13wk";
  snapshot_date: string;
  line_item: string;
  forecast: number;
  actual: number | null;
}

export interface TieOut {
  week_ending: string;
  statement_total: number;
  modelled_ending_cash: number;
  variance: number;
  ties: boolean;
}

export function useForecastGrid(params?: { horizonWeeks?: number; historyWeeks?: number }) {
  const horizon = params?.horizonWeeks ?? 13;
  const history = params?.historyWeeks ?? 12;
  return useQuery({
    queryKey: [...KEY, "forecast", horizon, history],
    queryFn: () =>
      apiFetch<ForecastGrid>(
        `/cash-flow/forecast?horizon_weeks=${horizon}&history_weeks=${history}`,
      ),
  });
}

export function useSnapshots() {
  return useQuery({
    queryKey: [...KEY, "snapshots"],
    queryFn: () => apiFetch<ForecastSnapshot[]>(`/cash-flow/snapshots`),
  });
}

export function useAccuracy() {
  return useQuery({
    queryKey: [...KEY, "accuracy"],
    queryFn: () => apiFetch<AccuracyRow[]>(`/cash-flow/accuracy`),
  });
}

export function useTieOut(weekEnding: string | null) {
  return useQuery({
    queryKey: [...KEY, "tie_out", weekEnding],
    queryFn: () => apiFetch<TieOut>(`/cash-flow/tie-out/${weekEnding}`),
    enabled: Boolean(weekEnding),
  });
}

export function useLockWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ weekEnding, force }: { weekEnding: string; force?: boolean }) =>
      apiFetch<LockResult>(`/cash-flow/lock/${weekEnding}`, {
        method: "POST",
        body: { force: force ?? false },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUnlockWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekEnding: string) =>
      apiFetch<ForecastSnapshot>(`/cash-flow/lock/${weekEnding}/unlock`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReapplyRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ReapplyResult>(`/cash-flow/rules/reapply`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}
